/**
 * Hooks API contrats formateur (Rafik 03/06/2026).
 *
 * - useContratsFormateurList(year)        → liste agrégée pour page Paramètres
 * - useGenerateContratCadre()             → mutation POST cadre
 * - useGenerateContratMission()           → mutation POST mission
 * - useCancelContrat()                    → mutation POST cancel
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';

export type ContratType = 'cadre' | 'mission';
export type IntervenantRole = 'formateur' | 'assistant-pedagogique';
export type ContratStatus = 'generated' | 'sent' | 'signed' | 'cancelled';

export interface ContratDTO {
  id: string;
  formateurId: string;
  type: ContratType;
  intervenantRole: IntervenantRole;
  apprenantZohoId: string | null;
  year: number;
  status: ContratStatus;
  driveFileId: string;
  driveFileName: string;
  driveWebViewUrl: string | null;
  tarifHoraire: number | null;
  nombreHeures: number | null;
  montantTotal: number | null;
  generatedAt: string;
  signedAt: string | null;
  apprenantNom?: string;
  apprenantPrenom?: string;
}

export interface FormateurRow {
  formateur: {
    id: string;
    nom: string;
    prenom: string;
    email: string;
    tarifHoraire: number;
    role: IntervenantRole;
    statut: string;
  };
  cadre: ContratDTO | null;
  missions: ContratDTO[];
  apprenantsAffilies: Array<{
    id: string;
    nom: string;
    prenom: string;
    nombreHeures: number;
    hasMission: boolean;
  }>;
}

interface ListResponse {
  year: number;
  formateurs: FormateurRow[];
}

const QK_LIST = (year: number) => ['contrats-formateur', 'list', year] as const;

export function useContratsFormateurList(year: number) {
  return useQuery({
    queryKey: QK_LIST(year),
    queryFn: () => api.get<ListResponse>(`/api/v1/v2/contrats-formateur?year=${year}`),
    staleTime: 30_000,
  });
}

export function useGenerateContratCadre(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { formateurId: string }) =>
      api.post<ContratDTO>('/api/v1/v2/contrats-formateur/cadre', {
        formateurId: input.formateurId,
        year,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST(year) });
    },
  });
}

export function useGenerateContratMission(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { formateurId: string; apprenantZohoId: string }) =>
      api.post<ContratDTO>('/api/v1/v2/contrats-formateur/mission', {
        ...input,
        year,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST(year) });
    },
  });
}

export function useCancelContrat(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      api.post<ContratDTO>(`/api/v1/v2/contrats-formateur/${input.id}/cancel`, {
        reason: input.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST(year) });
    },
  });
}
